// Librairies
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

// Components
import { ButtonComponent } from '../button/button.component';

// Interfaces
type CaptureMode = 'photo' | 'video';
// 'full' is 3:4 (portrait) / 4:3 (landscape); 'alt' is 1:1 (portrait) / 16:9 (landscape).
type Ratio = 'full' | 'alt';
type Facing = 'environment' | 'user';

@Component({
  selector: 'lib-camera-capture',
  imports: [CommonModule, MatIconModule, ButtonComponent, TranslateModule],
  templateUrl: './camera-capture.component.html',
  styleUrl: './camera-capture.component.scss'
})
export class CameraCaptureComponent implements AfterViewInit, OnDestroy {
  // Components API
  @Output() captured = new EventEmitter<File>();
  @Output() closed = new EventEmitter<void>();

  // Variables
  error: string | null = null;
  starting = true;
  // Torch is Chromium-only: iOS Safari never reports the capability, so the
  // control stays hidden there rather than sitting dead.
  hasTorch = false;
  flashOn = false;
  preview: string | null = null;
  previewKind: 'image' | 'video' | null = null;

  mode: CaptureMode = 'photo';
  ratio: Ratio = 'full';
  facing: Facing = 'environment';
  isLandscape = false;

  recording = false;
  finalizing = false;
  recordSeconds = 0;

  @ViewChild('video') private videoRef?: ElementRef<HTMLVideoElement>;
  private stream: MediaStream | null = null;
  private previewBlob: Blob | null = null;

  private static readonly MAX_VIDEO_BYTES = 10 * 1024 ** 3 - 64 * 1024 ** 2;
  private static readonly RECORDER_TIMESLICE_MS = 1000;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private recordedBytes = 0;
  private recordTimer: ReturnType<typeof setInterval> | null = null;
  private recordFrame: number | null = null;
  private recordStream: MediaStream | null = null;

  private orientationQuery?: MediaQueryList;
  // On rotation, snap back to the orientation's default ratio (16:9 landscape,
  // 3:4 portrait) — the 'full'/'alt' ratios don't map across orientations anyway.
  private onOrientation = (e: MediaQueryListEvent) => {
    this.isLandscape = e.matches;
    this.ratio = this.defaultRatio();
  };

  async ngAfterViewInit() {
    this.orientationQuery = window.matchMedia('(orientation: landscape)');
    this.isLandscape = this.orientationQuery.matches;
    this.ratio = this.defaultRatio();
    this.orientationQuery.addEventListener('change', this.onOrientation);
    await this.start();
  }

  // Landscape defaults to 16:9 ('alt'); portrait to 3:4 ('full').
  private defaultRatio(): Ratio {
    return this.isLandscape ? 'alt' : 'full';
  }

  ngOnDestroy() {
    this.orientationQuery?.removeEventListener('change', this.onOrientation);
    this.stopRecordTimer();
    this.stopRecordCanvas();
    this.stopCamera();
    this.clearPreview();
  }

  // The frame is letterboxed to a fixed ratio (default 3:4 upright / 4:3 wide,
  // or 1:1) so the phone camera's native 16:9 doesn't fill the whole screen.
  get ratioValue(): number {
    if (this.ratio === 'full') return this.isLandscape ? 4 / 3 : 3 / 4;
    return this.isLandscape ? 16 / 9 : 1;
  }

  get frameClass(): string {
    if (this.ratio === 'full') return this.isLandscape ? 'r-landscape' : 'r-portrait';
    return this.isLandscape ? 'r-wide' : 'r-square';
  }

  get ratioLabel(): string {
    if (this.ratio === 'full') return this.isLandscape ? '4:3' : '3:4';
    return this.isLandscape ? '16:9' : '1:1';
  }

  get timerLabel(): string {
    const s = this.recordSeconds;
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  private async start() {
    const video = this.videoRef?.nativeElement;
    if (!video) return;
    this.starting = true;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: this.facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        // Recording needs sound; a stream acquired in photo mode stays silent
        // until the user switches to video (which re-acquires with audio).
        audio: this.mode === 'video',
      });
      video.srcObject = this.stream;
      // Mute the live element in JS: the template `muted` attribute is unreliable
      // in Angular, so the mic would otherwise monitor out loud. The stream still
      // carries the audio track, so the recording (and its replay) keeps sound.
      video.muted = true;
      await video.play();

      const track = this.stream.getVideoTracks()[0];
      const capabilities = (track as any)?.getCapabilities?.();
      this.hasTorch = !!capabilities?.torch;
    } catch (err: any) {
      // getUserMedia also fails outside a secure context, which is what a phone
      // pointed at a plain-http dev server hits.
      this.error = err?.name === 'NotAllowedError' ? 'permission' : 'unavailable';
      console.warn('[camera] could not start:', err);
    } finally {
      this.starting = false;
    }
  }

  private stopCamera() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  private async restart() {
    this.stopCamera();
    this.flashOn = false;
    await this.start();
  }

  private clearPreview() {
    if (this.preview) URL.revokeObjectURL(this.preview);
    this.preview = null;
    this.previewKind = null;
    this.previewBlob = null;
  }

  private hasAudio(): boolean {
    return !!this.stream?.getAudioTracks().length;
  }

  async setMode(mode: CaptureMode) {
    if (this.mode === mode || this.recording) return;
    this.mode = mode;
    // Re-acquire with a mic the first time video is picked.
    if (mode === 'video' && !this.hasAudio()) await this.restart();
  }

  async switchCamera() {
    if (this.recording) return;
    this.facing = this.facing === 'environment' ? 'user' : 'environment';
    await this.restart();
  }

  toggleRatio() {
    this.ratio = this.ratio === 'full' ? 'alt' : 'full';
  }

  onShutter() {
    if (this.finalizing) return;
    if (this.mode === 'photo') {
      this.takePhoto();
      return;
    }
    this.recording ? this.stopRecording() : this.startRecording();
  }

  private takePhoto() {
    const video = this.videoRef?.nativeElement;
    if (!video?.videoWidth) return;

    // Center-crop the raw frame to the displayed ratio so the saved photo
    // matches what the letterboxed preview showed.
    const { cw, ch } = this.cropRect(video, this.ratioValue);
    const canvas = this.cropCanvas(cw, ch);
    this.drawCropped(canvas, video, this.ratioValue);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        this.previewBlob = blob;
        this.preview = URL.createObjectURL(blob);
        this.previewKind = 'image';
      },
      'image/jpeg',
      0.92,
    );
  }

  private cropRect(video: HTMLVideoElement, r: number) {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    let cw = vw;
    let ch = vh;
    if (vw / vh > r) cw = Math.round(vh * r);
    else ch = Math.round(vw / r);
    return { sx: Math.round((vw - cw) / 2), sy: Math.round((vh - ch) / 2), cw, ch };
  }

  private cropCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    if (this.facing === 'user') {
      // Front camera is previewed mirrored; save it the same way.
      const ctx = canvas.getContext('2d')!;
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
    }
    return canvas;
  }

  private drawCropped(canvas: HTMLCanvasElement, video: HTMLVideoElement, r: number) {
    if (!video.videoWidth) return;
    const { sx, sy, cw, ch } = this.cropRect(video, r);
    canvas.getContext('2d')!.drawImage(video, sx, sy, cw, ch, 0, 0, canvas.width, canvas.height);
  }

  private startRecording() {
    const video = this.videoRef?.nativeElement;
    if (!this.stream || !video?.videoWidth) return;
    this.chunks = [];
    this.recordedBytes = 0;

    const r = this.ratioValue;
    const { cw, ch } = this.cropRect(video, r);
    const canvas = this.cropCanvas(cw, ch);
    const draw = () => {
      this.drawCropped(canvas, video, r);
      this.recordFrame = requestAnimationFrame(draw);
    };
    draw();
    const stream = canvas.captureStream(30);
    this.stream.getAudioTracks().forEach((track) => stream.addTrack(track));
    this.recordStream = stream;

    this.recordedBytes = 0;
    const mime = this.pickVideoMime();
    try {
      this.recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    } catch {
      this.recorder = new MediaRecorder(stream);
    }
    const recorder = this.recorder;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) {
        this.chunks.push(e.data);
        this.recordedBytes += e.data.size;
      }
      if (recorder.state === 'recording' && this.recordedBytes >= CameraCaptureComponent.MAX_VIDEO_BYTES) {
        this.stopRecording();
      }
    };
    recorder.onstop = () => this.finishRecording();
    recorder.start(CameraCaptureComponent.RECORDER_TIMESLICE_MS);
    this.recording = true;
    this.recordSeconds = 0;
    this.recordTimer = setInterval(() => this.recordSeconds++, 1000);
  }

  private stopRecording() {
    this.finalizing = true;
    this.stopRecordTimer();
    try {
      this.recorder?.stop();
    } catch {
      /* already stopped */
    }
  }

  private stopRecordCanvas() {
    if (this.recordFrame !== null) cancelAnimationFrame(this.recordFrame);
    this.recordFrame = null;
    this.recordStream?.getVideoTracks().forEach((track) => track.stop());
    this.recordStream = null;
  }

  private finishRecording() {
    this.stopRecordCanvas();
    this.recording = false;
    this.finalizing = false;
    const type = this.recorder?.mimeType || 'video/webm';
    const blob = new Blob(this.chunks, { type });
    this.chunks = [];
    this.recorder = null;
    this.previewBlob = blob;
    this.preview = URL.createObjectURL(blob);
    this.previewKind = 'video';
  }

  private stopRecordTimer() {
    if (this.recordTimer) clearInterval(this.recordTimer);
    this.recordTimer = null;
  }

  private pickVideoMime(): string | null {
    const candidates = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    const supported = (window as any).MediaRecorder?.isTypeSupported;
    if (!supported) return null;
    return candidates.find((c) => MediaRecorder.isTypeSupported(c)) ?? null;
  }

  send() {
    if (!this.previewBlob) return;
    const isVideo = this.previewKind === 'video';
    const type = this.previewBlob.type || (isVideo ? 'video/webm' : 'image/jpeg');
    const ext = isVideo ? (type.includes('mp4') ? 'mp4' : 'webm') : 'jpg';
    const name = `${isVideo ? 'video' : 'photo'}-${Date.now()}.${ext}`;
    this.captured.emit(new File([this.previewBlob], name, { type }));
    // Back to the live view so several shots can be taken in a row.
    this.clearPreview();
  }

  discard() {
    this.clearPreview();
  }

  async toggleFlash() {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    const next = !this.flashOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] } as any);
      this.flashOn = next;
    } catch (err) {
      console.warn('[camera] torch not applicable:', err);
      this.hasTorch = false;
      this.flashOn = false;
    }
  }

  close() {
    if (this.recording) this.stopRecording();
    this.stopCamera();
    this.clearPreview();
    this.closed.emit();
  }
}
